/*
The StandardAppList.tsx file is a React component that renders a list of application tiles.
Each tile is represented by a card that includes the application's logo, title, and description. The tiles are styled using styled-components.
The StandardAppList component receives an array of tiles as a prop. 
Each tile object includes properties like tileId, themeColor, image, internalName, emulationEnabled, and targetURL.
The component maps over the tiles array and for each tile, it creates a StyledCard that includes an InnerAppWrapper, LogoContainer, AppBody, AppTitleWrapper, AppTitle, AppDescription, and AppLaunch button.
The AppLaunch button opens the application in a new browser tab when clicked. If the application does not support emulation and impersonation is active, the button is disabled and the description indicates that the application does not support emulation.
The component also receives impersonationActive and cardStyle as props. The cardStyle prop affects the styling of the card and the impersonationActive prop determines whether the application supports emulation.
The component uses media queries to adjust the styling based on the screen size. The appListSize prop, which is derived from the length of the tiles array, is also used to adjust the styling of the component.
*/

import {
  FC,
  Fragment,
  MouseEvent as ReactMouseEvent,
  useCallback,
} from 'react';
import styled from 'styled-components';
import Typography from 'shared/components/Typography';
import { EnhancedTile } from '../types';
import { ScreenSizeBreakpoints } from 'shared/constants';
import { Button, Card } from '@uhg-harmony/uitk-react';
import { cardThemeOptions } from 'builder/util/constants';
import { defaultTheme } from 'shared/theme/theme';

const AppBody = styled.p`
  display: block;
  position: absolute;
  bottom: 12px;
  transition: bottom '0.2s';
  left: 0;
  right: 0;
  height: 66%;
  padding-top: 20px;
`;

const LogoContainer = styled.div`
  display: grid;
  place-items: center;
  width: 90px;
`;
// const LogoContainer = styled.div`
//   display: flex;
//   justify-content: center; /* Horizontally center items */
//   align-items: flex-start; /* Align items to the start of the container (top) */
//   height: 100%; /* Set the container height to the full height of the parent div */
//   position: relative; /* Required for absolute positioning of the image */
// `;

const Logo = styled.img`
  margin: auto;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
`;

const StyledCard = styled.div<{ cardStyle: string }>`
  font-family: OptumSans, sans-serif;
  margin-top: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '-2.75rem' : '0'};
  width: ${p => (!p.cardStyle || p.cardStyle === 'Spacious' ? 'auto' : '25%')};
  padding: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '0' : '0 6px 12px'};
  box-sizing: border-box;

  @media (max-width: ${ScreenSizeBreakpoints.medium}px) {
    width: ${p =>
      !p.cardStyle || p.cardStyle === 'Spacious' ? 'auto' : '50%'};
  }

  @media (max-width: ${ScreenSizeBreakpoints.small}px) {
    width: ${p =>
      !p.cardStyle || p.cardStyle === 'Spacious' ? 'auto' : '100%'};
  }
`;

const InnerAppWrapper = styled(Card)<{
  themeColor: string;
  appListSize: number;
  disabled: boolean;
  cardStyle: string;
  cardTheme: string;
}>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '0 10px' : '0 auto'};
  ${p => (p.disabled ? defaultTheme.colors.aux.grey : p.themeColor)};
  width: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '320px' : '100%'};
  max-width: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '320px' : '248px'};
  height: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '408px' : '18rem'};
  position: relative;
  padding-top: 85px;
  margin-top: 65px;
  box-shadow: 0px 4px 8px #e3e3e3;
  &:hover {
    box-shadow: 0 0 8px 0 ${p => p.themeColor};
  }
  background-color: ${p =>
    !p.cardTheme || p.cardTheme === cardThemeOptions.Light
      ? '#ffffff'
      : '#212223'};
  border: 1px solid #373839;

  @media (max-width: ${ScreenSizeBreakpoints.medium}px) {
    width: ${p =>
      !p.cardStyle || p.cardStyle === 'Spacious' ? '228px' : '100%'};
    height: 208px;
  }
`;

const AppTitleWrapper = styled.div<{
  themeColor: string;
  cardStyle: string;
}>`
  color: ${defaultTheme.colors.aux.black};
  text-align: center;
  width: 100%;
  height: ${p => (!p.cardStyle || p.cardStyle === 'Spacious' ? '18%' : '25%')};
  margin-top: ${p =>
    !p.cardStyle || p.cardStyle === 'Spacious' ? '0rem' : '3rem'};
  background: transparent;

  @media (max-width: ${ScreenSizeBreakpoints.medium}px) {
    position: relative;
    bottom: -10px;
  }
`;

const AppTitle = styled(Typography)<{
  appListSize: number;
  cardTheme: string;
}>`
  font-family: OptumSans, sans-serif;
  color: ${p =>
    !p.cardTheme || p.cardTheme === cardThemeOptions.Light
      ? `${defaultTheme.colors.aux.black}`
      : '#ffffff'};
  text-align: center;
  height: 35px;
  padding: ${p => (p.appListSize >= 5 ? '0 14px' : '0 8px')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  && {
    line-height: 20px;
    font-size: 18px;
    font-weight: 700;
    height: 100%;

    @media (max-width: ${ScreenSizeBreakpoints.small}px) {
      line-height: 40px;
    }

    @media (max-width: ${ScreenSizeBreakpoints.medium}px) {
      padding: 0 14px;
      line-height: normal;
    }
  }
`;

const AppDescription = styled(Typography)<{ appListSize: number }>`
  font-family: 'OptumSans' !important;
  -webkit-line-clamp: ${p => (p.appListSize >= 5 ? '4' : '3')};
  -webkit-box-orient: vertical;
  width: 100%;
  text-align: left;
  color: ${defaultTheme.colors.aux.black};
  background: none;
  padding: 0rem 2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  height: ${p => (p.appListSize >= 5 ? '65%' : '70%')};
  &:hover {
    white-space: normal;
  }
  @media (max-width: ${ScreenSizeBreakpoints.medium}px) {
    height: 55%;
    -webkit-line-clamp: 4;
  }
`;

const AppLaunch = styled(Button)<{ themeColor: string; disabled: boolean }>`
  position: absolute;
  display: flex;
  justify-content: center;
  bottom: 25px;
  left: 30px;
  right: 0px;
  width: 80%;
  color: #282a2e;
  text-align: center;
  font-family: 'OptumSans-Regular', 'OptumSans', 'sans-serif';
  font-color: #19191a;
  line-height: 30px;
  text-decoration: none;
  padding: 8px 16px 8px 16px;
  &:hover,
  &:focus {
    cursor: pointer;
    background: ${p => p.themeColor};
    color: #fff;
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 5px ${p => p.themeColor};
    border: 0;
  }
  font-weight: normal;
`;

/**
 * StandardAppList
 * Renders a collection of "standard" application tiles. Each tile exposes two user interaction targets:
 *  - The application title (for analytics tracking only)
 *  - The launch button (for navigation + analytics tracking)
 *
 * The supplied handleClick callback is invoked for both interactions so that upstream widgets (MyApps)
 * can emit a unified tracking payload. Historically this callback was passed but never used which
 * prevented tile click tracking tests from succeeding. This refactor restores the contract without
 * altering visual rendering.
 *
 * Accessibility: AppTitle is given role="button" with a tabindex to make it focusable & clickable
 * for keyboard users, while the actual launch still occurs via the styled Button component.
 */
const StandardAppList: FC<{
  tiles: Array<EnhancedTile>;
  handleClick: (e: any, tile: EnhancedTile) => any; // Accept React or native mouse events
  impersonationActive: boolean;
  borderColor: string; // currently unused; reserved for future border styling
  cardStyle: string;
  cardTheme: string;
  currentPage: PageVersion;
}> = ({
  tiles,
  handleClick,
  impersonationActive,
  cardStyle,
  cardTheme,
  borderColor: _borderColor,
  currentPage: _currentPage,
}) => {
  /**
   * Opens an application tile using the target provided by MyApps.
   *
   * For `_blank` launches, the security features prevent the opened app from
   * retaining access to this Horizon window through `window.opener` and omit
   * the referrer for older browser compatibility. A `false` return means the
   * launch was blocked by the browser or failed before a window could be opened.
   *
   * @param tile - Tile metadata returned by MyApps, including target URL and browsing-context target.
   * @returns `true` when the browser reports a window/navigation handle; otherwise `false`.
   */
  const openTile = (tile: EnhancedTile): boolean => {
    const target = tile.target || '_self';

    try {
      const openedWindow =
        target === '_blank'
          ? window.open(tile.targetURL, target, 'noopener,noreferrer')
          : window.open(tile.targetURL, target);

      if (!openedWindow) {
        console.warn('MyApps tile launch was blocked or failed to open.', {
          tileId: tile.tileId,
          target,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.warn('MyApps tile launch failed.', {
        tileId: tile.tileId,
        target,
        error,
      });
      return false;
    }
  };

  const invokeHandleClick = useCallback(
    (e: ReactMouseEvent<unknown>, tile: EnhancedTile) => {
      handleClick(e, tile);
    },
    [handleClick]
  );
  return (
    <Fragment>
      {tiles.map(tile => (
        <StyledCard cardStyle={cardStyle} key={`tile-wrapper-${tile.tileId}`}>
          <InnerAppWrapper
            appListSize={tiles.length}
            key={tile.tileId}
            themeColor={tile.themeColor}
            data-test-id="tile"
            disabled={impersonationActive && !tile.emulationEnabled}
            cardStyle={cardStyle}
            cardTheme={cardTheme}
          >
            <LogoContainer>
              <Logo
                data-test-id={`tile-logo-${tile.tileId}`}
                src={tile.image}
              />
            </LogoContainer>
            <AppBody>
              <AppTitleWrapper
                themeColor={tile.themeColor}
                cardStyle={cardStyle}
              >
                <AppTitle
                  appListSize={tiles.length}
                  cardTheme={cardTheme}
                  data-test-id={`tile-title-${tile.tileId}`}
                  variant="headline"
                  onClick={e => invokeHandleClick(e, tile)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      invokeHandleClick(e as any, tile);
                    }
                  }}
                >
                  {tile.label}
                </AppTitle>
              </AppTitleWrapper>
              {(!cardStyle || cardStyle === 'Spacious') && (
                <AppDescription
                  appListSize={tiles.length}
                  data-test-id="tile-description"
                  variant="body1"
                  gutterBottom
                >
                  {impersonationActive && !tile.emulationEnabled
                    ? 'Application does not support emulation.'
                    : tile.description}
                </AppDescription>
              )}
            </AppBody>
            <AppLaunch
              themeColor={tile.themeColor}
              data-test-id={`tile-launch-${tile.tileId}`}
              variant="tertiary"
              disabled={impersonationActive && !tile.emulationEnabled}
              onPress={
                (!impersonationActive || tile.emulationEnabled) &&
                ((e: any) => {
                  invokeHandleClick(e, tile);
                  openTile(tile);
                })
              }
            >
              Launch Application
            </AppLaunch>
          </InnerAppWrapper>
        </StyledCard>
      ))}
    </Fragment>
  );
};

export default StandardAppList;
